// src/features/treinos/components/muscle-map/muscleMapConfig.js
//
// Ponto ÚNICO de mapeamento do MuscleMap. Nada de strings/ids espalhados
// pelo componente ou pelos utils — tudo que traduz "domínio → SVG" mora aqui.
//
//   1) MUSCLE_SVG_IDS      → chave lógica (inglês) → ids reais dentro dos SVGs.
//                            Portado VERBATIM do protótipo (script.js:32).
//   2) CANONICAL_TO_LOGICAL→ valor canônico PT do Firestore/exercise_library
//                            → chave lógica acima. ÚNICO tradutor PT→lógico.
//   3) STATE_COLORS        → as 8 cores de estado (consumidas pelo CSS Module).
//   4) STATE_CLASS_MAP     → estado canônico → classes CSS aplicadas no <g>.
//   5) SVG_VIEWBOX / GRADIENT_DEFS_MARKUP → metadados do SVG p/ injeção inline.

// ─────────────────────────────────────────────────────────────────────────────
// 1) MAPEAMENTO LÓGICO → IDS DO SVG  (FONTE AUTORITATIVA — script.js:32)
//
// ⚠️ NÃO renomear nenhum id. Alguns lados espelhados usam id genérico do Figma
//    com escopo "front:" / "back:" (resolvidos só naquele SVG). O tagging em
//    runtime (prepareMuscleGroups) replica a abordagem do protótipo.
// ─────────────────────────────────────────────────────────────────────────────
export const MUSCLE_SVG_IDS = {
  chest:      ["front-chest"],
  shoulders:  [
    "front-deltoid-anterior",
    "front:Group 575",      // deltoide anterior direito (espelho sem nome)
    "front-deltoid-lateral",
    "back-deltoid-lateral",
    "front-deltoid-posterior",
    "back:Vector_15",       // deltoide posterior direito (espelho sem nome)
  ],
  biceps:     ["front-biceps"],
  triceps:    ["front-triceps", "back-triceps"],
  forearms:   [
    "front-forearms-wrist-extensors",
    "front-forearms-wrist-flexors",
    "front-brachioradialis",
    "back-forearms-wrist-extensors",
    "back-forearms-wrist-flexors",
  ],
  core:       ["front-rectus-abdominis", "front-obliques", "back-obliques"],
  back:       ["back-back", "front-back"],
  traps:      [
    "front-trapezius",
    "back-trapezius",
    "back:Vector_17",       // trapézio superior direito (espelho sem nome)
    "back:Vector_18",       // trapézio inferior esquerdo
    "back:Vector_19",       // trapézio inferior direito
  ],
  quads:      ["front-quadriceps", "back-quadriceps"],
  hamstrings: ["back-hamstrings"],
  glutes:     ["back-gluteus-maximus"],
  calves:     ["front-calves", "back-calves"],
  // grupos secundários — recebem tooltip/cor, mas raramente são focados
  adductors:  ["front-hip-adductors", "back-hip-adductors"],
  abductors:  ["front-abductors", "back-abductors"],
  tibialis:   ["front-tibialis-anterior"],
};

// ─────────────────────────────────────────────────────────────────────────────
// 2) CANÔNICO PT (Firestore / exercise_library) → CHAVE LÓGICA
//
// ÚNICO ponto que traduz o muscle_group canônico em PT (valor real gravado em
// muscle_group / secondary_muscle_groups) para chave(s) lógica(s). O lookup é
// feito SEMPRE via muscleMapUtils.resolveCanonicalToLogical, que normaliza os
// DOIS lados (lowercase + sem acento + "_"→espaço) — então o Firestore pode
// mandar "PEITO"/"Tríceps" e uma chave nova aqui pode até ter acento que casa
// igual. Canônico desconhecido gera console.warn (nunca cinza silencioso).
//
// VALOR = ARRAY de chaves lógicas (um canônico pode acender vários grupos).
//   • "inferiores" acende a PERNA INTEIRA (quads+hamstrings+glutes) — é o teto
//     do dado disponível (não há granularidade maior). Ajuste aqui se mudar.
//   • "aerobio" / "funcional" / "mobilidade" → [] (não pintam nenhum grupo).
//     Ajustáveis aqui caso queira mapeá-los no futuro.
// ─────────────────────────────────────────────────────────────────────────────
export const CANONICAL_TO_LOGICAL = {
  // — peito —
  peito:        ["chest"],
  peitoral:     ["chest"],
  peitorais:    ["chest"],
  // — costas / trapézio —
  costas:       ["back", "traps"],
  dorsal:       ["back"],
  dorsais:      ["back"],
  dorso:        ["back"],
  lombar:       ["back"],
  trapezio:     ["traps"],
  trapezios:    ["traps"],
  // — ombros —
  ombro:        ["shoulders"],
  ombros:       ["shoulders"],
  deltoide:     ["shoulders"],
  deltoides:    ["shoulders"],
  // — braços —
  biceps:       ["biceps"],
  triceps:      ["triceps"],
  braco:        ["biceps", "triceps"],
  bracos:       ["biceps", "triceps"],
  antebraco:    ["forearms"],
  antebracos:   ["forearms"],
  // — core —
  abdomen:      ["core"],
  abdome:       ["core"],
  abdominal:    ["core"],
  abdominais:   ["core"],
  core:         ["core"],
  // — membros inferiores —
  inferiores:   ["quads", "hamstrings", "glutes"],
  perna:        ["quads", "hamstrings", "glutes"],
  pernas:       ["quads", "hamstrings", "glutes"],
  coxa:         ["quads", "hamstrings"],
  coxas:        ["quads", "hamstrings"],
  quadriceps:   ["quads"],
  posterior:    ["hamstrings"],
  posteriores:  ["hamstrings"],
  isquiotibiais:["hamstrings"],
  gluteo:       ["glutes"],
  gluteos:      ["glutes"],
  panturrilha:  ["calves"],
  panturrilhas: ["calves"],
  gemeos:       ["calves"],
  // — sem pintura (cardio / genéricos) —
  aerobio:      [],
  aerobico:     [],
  cardio:       [],
  corrida:      [],
  hiit:         [],
  funcional:    [],
  mobilidade:   [],
  descanso:     [],
};

// ─────────────────────────────────────────────────────────────────────────────
// 3) AS 8 CORES DE ESTADO
//
// Valores representativos (topo do gradiente do protótipo). O CSS Module usa
// gradientes inline para o preenchimento; estas cores ficam disponíveis em JS
// (legenda, mini-preview, badges) e como --mm-* custom props no .module.css.
// ─────────────────────────────────────────────────────────────────────────────
// Os estados "amarelos" derivam de var(--color-accent) (segue a academia),
// preservando a rampa de brilho (low escuro → high claro) via color-mix.
// Estados semânticos não-amarelos (recovery/ready/red) ficam fixos.
// REPALETA 12-prompt § 5. Uma decisão atravessa a tabela toda: o que é
// SEMÂNTICO virou hex fixo (vermelho é cansaço, verde é pronto, azul é
// recuperando — isso não muda por academia), e o que é MARCA continua derivado
// de `var(--color-accent)`, senão o white-label perdia a cor do cliente dentro
// do mapa. O handoff escreve foco #FBBD04, que é exatamente o acento padrão da
// LesVita: para o tenant padrão os dois coincidem, e para um cliente
// white-label o foco aparece na cor dele — que é a leitura certa de destaque.
export const STATE_COLORS = {
  inactive: "#262C3A", // § 5: músculo inativo
  low:      "color-mix(in srgb, var(--color-accent), #000 45%)", // marca, escuro
  medium:   "#E0A800", // § 5: médio
  high:     "#F0564A", // § 5: alto (cansaço alto lê melhor em vermelho)
  recovery: "#8FA2FF", // § 5: recuperando
  ready:    "#3ECF8E", // § 5: pronto
  trained:  "#3A55E0", // § 5: treinado (sai do amarelo — azul é o destaque agora)
  focus:    "color-mix(in srgb, var(--color-accent), #fff 12%)", // marca, claro
  red:      "#F0564A", // alinhado ao `high`: é o mesmo significado
};

// Lista canônica dos estados aceitos na prop `muscles`.
export const MUSCLE_STATES = [
  "inactive", "low", "medium", "high", "recovery", "ready", "trained", "focus",
  "red", "selected",
];

// Tom visual por RANK de foco do treino (1 = grupo com MAIS exercícios).
// Ponto ÚNICO e tunável — consumido pelas telas (ex.: Foco muscular do
// TreinoDetalhes) p/ pintar o MuscleMap e colorir a legenda via STATE_COLORS.
export const TONE_BY_RANK = {
  1: "high",      // amarelo (foco principal)
  2: "recovery",  // laranja
  3: "red",       // vermelho
  default: "medium",
};

// Prioridade p/ merge (getPlanMuscleMap): maior vence ao agregar treinos.
export const STATE_PRIORITY = {
  inactive: 0,
  low:      1,
  ready:    2,
  recovery: 3,
  medium:   4,
  red:      5,
  trained:  5,
  high:     6,
  focus:    7,
  // Acima de tudo: seleção é escolha explícita da pessoa e não pode ser
  // apagada por um estado de fadiga calculado no merge.
  selected: 99,
};

// ─────────────────────────────────────────────────────────────────────────────
// 4) ESTADO CANÔNICO → CLASSES CSS aplicadas no <g class="muscle-group">
//
// Reusa as classes do protótipo (m-low/m-medium/m-high/m-recovery/m-ready/
// m-st-trained/m-st-focus). "inactive" = sem classe (base escura padrão).
// ─────────────────────────────────────────────────────────────────────────────
export const STATE_CLASS_MAP = {
  inactive: [],
  low:      ["m-low"],
  medium:   ["m-medium"],
  high:     ["m-high"],
  recovery: ["m-recovery"],
  ready:    ["m-ready"],
  trained:  ["m-high", "m-st-trained"], // base amarela + contorno destaque sutil
  focus:    ["m-high", "m-st-focus"],   // base amarela + pulse (destaque principal)
  red:      ["m-tone-red"],             // tom explícito vermelho (rank 3)
  // Variante `select` (§ 5): não é fadiga, é escolha da pessoa. Entra como
  // estado próprio para que a limpeza de classes (ALL_STATE_CLASSES) também a
  // remova, em vez de ficar grudada quando o grupo é desmarcado.
  selected: ["m-selected"],
};

// Todas as classes de estado (p/ limpar antes de repintar).
export const ALL_STATE_CLASSES = Array.from(
  new Set(Object.values(STATE_CLASS_MAP).flat()),
);

// ─────────────────────────────────────────────────────────────────────────────
// 5) METADADOS DOS SVGs + DEFS DE GRADIENTE
//
// viewBox original preservado (largura/altura removidas em runtime p/ fluidez).
// Os gradientes vivem fora dos .svg (no protótipo estavam no index.html); são
// injetados inline no topo de cada SVG durante o tagging — assim cada SVG fica
// auto-contido e o url(#gradX) do CSS resolve dentro do próprio documento.
// ─────────────────────────────────────────────────────────────────────────────
export const SVG_VIEWBOX = {
  front: "0 0 587 1137",
  back:  "0 0 596 1133",
};

// ─────────────────────────────────────────────────────────────────────────────
// 6) RECORTE POR REGIÃO  (prop `region` do MuscleMap)
//
// Frações [fx, fy, fw, fh] aplicadas sobre o viewBox de cada lado — o recorte
// é SÓ o atributo viewBox do <svg> renderizado (nenhum path/id alterado).
// TUNÁVEL num ponto só: a linha da cintura foi calibrada a olho sobre os SVGs
// oficiais; upper/lower se SOBREPÕEM de propósito (0.42–0.60) para o corte não
// decapitar core/glúteos na transição. Ajuste as frações aqui se necessário.
// ─────────────────────────────────────────────────────────────────────────────
export const REGION_CROP = {
  full:  [0, 0,    1, 1],
  upper: [0, 0,    1, 0.60],
  lower: [0, 0.42, 1, 0.58],
};

// Chave lógica → região anatômica (consumido por region="auto":
// todos-upper → 'upper'; todos-lower → 'lower'; misto/vazio → 'full').
export const LOGICAL_REGION = {
  chest: "upper", shoulders: "upper", biceps: "upper", triceps: "upper",
  forearms: "upper", core: "upper", back: "upper", traps: "upper",
  quads: "lower", hamstrings: "lower", glutes: "lower", calves: "lower",
  adductors: "lower", abductors: "lower", tibialis: "lower",
};

export const GRADIENT_DEFS_MARKUP = `
<defs>
  <linearGradient id="gradInactive" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stop-color="#262C3A"/><stop offset="1" stop-color="#1F242F"/>
  </linearGradient>
  <linearGradient id="gradLow" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" style="stop-color:color-mix(in srgb, var(--color-accent), #000 40%)"/><stop offset="1" style="stop-color:color-mix(in srgb, var(--color-accent), #000 55%)"/>
  </linearGradient>
  <linearGradient id="gradMedium" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stop-color="#EFB715"/><stop offset="1" stop-color="#E0A800"/>
  </linearGradient>
  <linearGradient id="gradHigh" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stop-color="#F4695E"/><stop offset="1" stop-color="#E0453A"/>
  </linearGradient>
  <linearGradient id="gradRecovery" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stop-color="#A8B6FF"/><stop offset="1" stop-color="#7A8EF0"/>
  </linearGradient>
  <linearGradient id="gradReady" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stop-color="#5BDCA4"/><stop offset="1" stop-color="#2FB87A"/>
  </linearGradient>
  <linearGradient id="gradRed" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stop-color="#F4695E"/><stop offset="1" stop-color="#E0453A"/>
  </linearGradient>
  <!-- Selecao (variante select do 12-prompt 5): o azul da marca, em
       diagonal. E o unico gradiente diagonal do mapa, de proposito: selecao e
       escolha da pessoa, e se le diferente de estado de fadiga. -->
  <linearGradient id="gradSelected" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0" stop-color="#3A55E0"/><stop offset="1" stop-color="#8FA2FF"/>
  </linearGradient>
</defs>`;

// Lista lógica → rótulo PT curto (legenda / tooltip). Derivado, não autoritativo.
export const LOGICAL_LABELS_PT = {
  chest: "Peito", shoulders: "Ombros", biceps: "Bíceps", triceps: "Tríceps",
  forearms: "Antebraços", core: "Core", back: "Costas", traps: "Trapézio",
  quads: "Quadríceps", hamstrings: "Posteriores", glutes: "Glúteos",
  calves: "Panturrilhas", adductors: "Adutores", abductors: "Abdutores",
  tibialis: "Tibial",
};
